import { useMemo, useRef, useState } from "react";
import { ChevronDown, Star, Trash2, Search, History as HistoryIcon, Pin, PinOff, Pencil } from "lucide-react";

const CATEGORY_ORDER = [
  ["explore", "Explore"],
  ["assistant", "Assistants"],
  ["tutor", "Tutor"],
  ["elite", "Elite Tools"],
  ["storyteller", "Storytelling"],
  ["general", "General"],
];

const ago = (iso) => {
  const mins = Math.max(Math.round((Date.now() - new Date(iso).getTime()) / 60000), 0);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Sidebar history: every past search grouped by the prompt it came from, plus pins and a Saved tab.
export const ChatHistory = ({ sessions, promptMeta, activeId, onSelect, onDelete, onToggleSave, onTogglePin, onRename }) => {
  const [tab, setTab] = useState("recent");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const lastClick = useRef({ id: null, at: 0 });

  const meta = (persona) =>
    promptMeta[persona] || { label: persona || "General", category: "general" };

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = sessions.filter((s) => {
      if (tab === "saved" ? !s.saved : !s.message_count) return false;
      if (!q) return true;
      return `${s.title} ${meta(s.persona).label}`.toLowerCase().includes(q);
    });
    const pinned = rows.filter((s) => s.pinned);
    return [
      { id: "pinned", label: "Pinned", rows: pinned },
      ...CATEGORY_ORDER.map(([id, label]) => ({
        id,
        label,
        rows: rows.filter((s) => !s.pinned && meta(s.persona).category === id),
      })),
    ].filter((g) => g.rows.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, tab, query, promptMeta]);

  const savedCount = sessions.filter((s) => s.saved).length;
  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  const startRename = (s) => {
    setEditing(s.session_id);
    setDraft(s.title);
  };

  // Own double-click detection: React's onDoubleClick is unreliable once the first click re-renders the row.
  const handleRowClick = (s) => {
    const at = Date.now();
    if (lastClick.current.id === s.session_id && at - lastClick.current.at < 400) {
      lastClick.current = { id: null, at: 0 };
      startRename(s);
      return;
    }
    lastClick.current = { id: s.session_id, at };
    onSelect(s);
  };

  const commitRename = (s) => {
    const name = draft.trim();
    setEditing(null);
    if (name && name !== s.title) onRename(s, name);
  };

  return (
    <div className="mt-5" data-testid="chat-history">
      <p className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-ink-soft">
        <HistoryIcon className="h-3.5 w-3.5" /> History
      </p>
      <div className="flex gap-1 rounded-full bg-surfacealt p-1">
        {[
          ["recent", "Recent"],
          ["saved", `Saved${savedCount ? ` (${savedCount})` : ""}`],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`history-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
              tab === id ? "bg-brand text-white" : "text-ink-soft hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative mt-3">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
        <input
          data-testid="history-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your history…"
          className="min-h-[38px] w-full rounded-full border border-white/10 bg-page ps-9 pe-3 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
        />
      </div>

      <div className="mt-3 space-y-3" data-testid="chat-session-list">
        {groups.map((g) => (
          <div key={g.id} data-testid={`history-group-${g.id}`}>
            <button
              data-testid={`history-group-toggle-${g.id}`}
              onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))}
              className={`flex w-full items-center gap-1.5 px-1 py-1 text-start text-[11px] font-semibold uppercase tracking-widest transition-colors hover:text-ink ${
                g.id === "pinned" ? "text-brand" : "text-ink-soft"
              }`}
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${collapsed[g.id] ? "-rotate-90" : ""}`}
              />
              {g.id === "pinned" && <Pin className="h-3 w-3" />}
              {g.label}
              <span className="ms-auto rounded-full bg-surfacealt px-2 py-0.5 text-[10px]">{g.rows.length}</span>
            </button>
            {!collapsed[g.id] && (
              <div className="mt-1 space-y-1.5">
                {g.rows.map((s) => (
                  <div
                    key={s.session_id}
                    className={`group flex items-center gap-1.5 rounded-2xl border px-3 py-2.5 transition-colors ${
                      activeId === s.session_id
                        ? "border-brand/30 bg-panel"
                        : "border-transparent hover:bg-panel"
                    }`}
                  >
                    {editing === s.session_id ? (
                      <input
                        data-testid={`rename-input-${s.session_id}`}
                        autoFocus
                        value={draft}
                        maxLength={120}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={() => commitRename(s)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(s);
                          if (e.key === "Escape") setEditing(null);
                        }}
                        className="min-w-0 flex-1 rounded-lg border border-brand/50 bg-page px-2 py-1 text-sm outline-none"
                      />
                    ) : (
                      <>
                        <button
                          data-testid={`session-${s.session_id}`}
                          onClick={() => handleRowClick(s)}
                          className="min-w-0 flex-1 text-start"
                        >
                          <span dir="auto" className="block truncate text-sm font-medium">{s.title}</span>
                          <span className="block truncate text-xs text-ink-soft">
                            {meta(s.persona).label} · {ago(s.updated_at)}
                          </span>
                        </button>
                        <button
                          data-testid={`rename-session-${s.session_id}`}
                          onClick={() => startRename(s)}
                          aria-label="Rename this search"
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          <Pencil className="h-3.5 w-3.5 text-ink-soft hover:text-brand" />
                        </button>
                        <button
                          data-testid={`pin-session-${s.session_id}`}
                          onClick={() => onTogglePin(s)}
                          aria-label={s.pinned ? "Unpin this search" : "Pin this search to the top"}
                          className={s.pinned ? "" : "opacity-0 transition-opacity group-hover:opacity-100"}
                        >
                          {s.pinned ? (
                            <PinOff className="h-3.5 w-3.5 text-brand" />
                          ) : (
                            <Pin className="h-3.5 w-3.5 text-ink-soft hover:text-brand" />
                          )}
                        </button>
                        <button
                          data-testid={`save-session-${s.session_id}`}
                          onClick={() => onToggleSave(s)}
                          aria-label={s.saved ? "Remove from saved" : "Save this search"}
                          className={s.saved ? "" : "opacity-0 transition-opacity group-hover:opacity-100"}
                        >
                          <Star
                            className={`h-3.5 w-3.5 ${
                              s.saved ? "fill-brand text-brand" : "text-ink-soft hover:text-brand"
                            }`}
                          />
                        </button>
                        <button
                          data-testid={`delete-session-${s.session_id}`}
                          onClick={() => onDelete(s)}
                          aria-label="Delete this search"
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-ink-soft hover:text-destructive" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {total === 0 && (
          <p className="px-1 text-xs text-ink-soft" data-testid="history-empty">
            {tab === "saved"
              ? "Nothing saved yet — tap the star on any search to keep it here."
              : query
              ? "No searches match that."
              : "Your searches will show up here, grouped by the prompt you used."}
          </p>
        )}
      </div>
    </div>
  );
};
